import React, { useState, useEffect } from "react";
import { Box, Button, Divider, Typography, useMediaQuery, useTheme } from "@mui/material";
import { RouteComponentProps } from "react-router-dom";

import { ICONS } from "@APP/assets";
import { PageLayout, Message, ErrorMessage, RTPSummary } from "@APP/components";
import { useAppSelector } from "@APP/redux";
import { history } from "@APP/navigation";
import { API } from "@APP/services";
import { DESKTOP_CTA_SX } from "@APP/styles/cta";
import { MOBILE_CTA_HEIGHT, MOBILE_FONT, MOBILE_ICON } from "@APP/styles/mobile";
import { DESKTOP_STATUS } from "@APP/styles/status";

interface QueryParams {
  requestId: string;
}

/**
 * The desktop confirm-step alert circle. The brand SVG is authored at 128px
 * with a 104px circle inside it, so it is rendered at its natural size — the
 * same optical weight as the status screens' icons.
 */
const DESKTOP_ALERT_ICON = 128;

const DeclineRTPScreen: React.FC<RouteComponentProps> = ({ match }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const params = match.params as QueryParams;
  const { requestId } = params;

  const rtpState = useAppSelector((state) => state.rtp);

  const [declineRequestState, setDeclineRequestState] = useState({
    error: false,
    loading: false,
    success: false,
  });

  useEffect(() => {
    if (!rtpState.rtpData) {
      history.push("/");
    }
  }, [rtpState.rtpData]);

  const handleDecline = async () => {
    setDeclineRequestState((prevState) => ({ ...prevState, loading: true }));
    try {
      requestId && (await API.declineRTP(requestId));
      setDeclineRequestState((prevState) => ({ ...prevState, success: true }));
    } catch (err) {
      setDeclineRequestState((prevState) => ({ ...prevState, error: true }));
    }
    setDeclineRequestState((prevState) => ({ ...prevState, loading: false }));
  };

  const handleGoBack = () => {
    history.goBack();
  };
  const handleClose = () => {
    window.close();
  };

  const renderMainContent = () => {
    if (declineRequestState.success) {
      // Terminal state from the LBG design: the green declined flag, the
      // confirmation headline, then a full-width rule with a single Close
      // action under it. No summary or description card.
      return (
        <>
          <Message
            style={{ margin: "auto" }}
            type="success"
            title="You have declined this payment request"
            iconSrc={ICONS.DECLINE_ICON}
            iconSize={isMobile ? MOBILE_ICON.success : undefined}
          />
          {/* `Message` already carries 24px of bottom padding on desktop, so
              the rule only adds the remainder of the design's title-to-rule
              gap; below it the design leaves a clear 40px before the CTA. */}
          <Divider
            sx={{
              width: "100%",
              mt: isMobile ? "20px" : undefined,
              mb: isMobile ? "28px" : "24px",
            }}
          />
          {/* Desktop right-aligns the CTA under the rule; mobile stacks it
                    full width like the other journey screens. */}
          <Box display="flex" justifyContent={isMobile ? "stretch" : "flex-end"} width="100%">
            <Button
              fullWidth={isMobile}
              sx={isMobile ? { minHeight: MOBILE_CTA_HEIGHT } : DESKTOP_CTA_SX}
              color="primary"
              variant="contained"
              id="declineRtpScreenCloseButton"
              onClick={handleClose}>
              Close
            </Button>
          </Box>
        </>
      );
    }

    if (declineRequestState.error) {
      return <ErrorMessage />;
    }

    // The confirm step from the LBG design: the red alert circle, a centred
    // headline, the payment request and contact cards, then the two actions
    // right-aligned under them (stacked full width on mobile).
    return (
      <>
        <img
          src={ICONS.ERROR_ICON}
          style={{
            width: isMobile ? MOBILE_ICON.error : DESKTOP_ALERT_ICON,
            height: isMobile ? MOBILE_ICON.error : DESKTOP_ALERT_ICON,
            objectFit: "contain",
          }}
          alt=""
          id="declineRtpScreenImage"
        />
        <Box
          width="100%"
          mt={isMobile ? "16px" : `${DESKTOP_STATUS.ICON_GAP}px`}
          mb={isMobile ? "24px" : `${DESKTOP_STATUS.TITLE_GAP}px`}>
          <Typography
            component="h2"
            variant="h4"
            align="center"
            sx={{
              fontSize: { xs: `${MOBILE_FONT.subHeadline}px`, sm: "30px" },
              lineHeight: 1.2,
              maxWidth: { xs: 300, sm: "none" },
              marginInline: "auto",
            }}
            id="declineRtpScreenTypo">
            Are you sure you want to decline this payment request?
          </Typography>
        </Box>
        <RTPSummary rtpState={rtpState} />
        {isMobile ? (
          // Mobile keeps the journey's stacked CTAs, primary action first.
          <>
            <Divider
              sx={{
                width: "100%",
                mt: "54px",
                mb: "16px",
              }}
            />
            <Box display="flex" flexDirection="column" gap={2} width="100%" mt="24px">
              <Button
                fullWidth
                sx={{ minHeight: MOBILE_CTA_HEIGHT }}
                color="primary"
                id="declineRtpScreenDeclineButton"
                variant="contained"
                onClick={handleDecline}>
                Yes, decline
              </Button>
              <Button
                fullWidth
                sx={{ minHeight: MOBILE_CTA_HEIGHT }}
                id="declineRtpScreenGoBackButton"
                variant="outlined"
                color="secondary"
                onClick={handleGoBack}>
                No, go back
              </Button>
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            width="100%"
            mt={`${DESKTOP_STATUS.ACTIONS_GAP}px`}>
            <Button
              sx={DESKTOP_CTA_SX}
              id="declineRtpScreenGoBackButton"
              variant="outlined"
              color="secondary"
              onClick={handleGoBack}>
              No, go back
            </Button>
            <Button
              sx={DESKTOP_CTA_SX}
              color="primary"
              id="declineRtpScreenDeclineButton"
              variant="contained"
              onClick={handleDecline}>
              Yes, decline
            </Button>
          </Box>
        )}
      </>
    );
  };

  // The "declined" terminal state drops the header on mobile and keeps it on
  // desktop, matching the other status screens.
  const hideHeader = isMobile && declineRequestState.success;

  // The confirm and error steps show no footer in the design; the "declined"
  // terminal state keeps the minimal bar like the other status screens.
  const hideFooter = !declineRequestState.success;

  return (
    <PageLayout
      loading={declineRequestState.loading}
      hideHeader={hideHeader}
      hideFooter={hideFooter}
      footerVariant="minimal">
      <Box display="flex" alignItems="center" flexDirection="column" width="100%">
        {renderMainContent()}
      </Box>
    </PageLayout>
  );
};

export default DeclineRTPScreen;
